import { useState, type FormEvent } from "react";
import { Button } from "@/shared/components/ui/Button";
import { PasswordFormField } from "@/shared/components/form/PasswordFormField";
import { useChangePassword } from "@/shared/lib/auth";

/**
 * Change the account password. Requires the current password; on success it
 * signs out other devices. Confirms the new password client-side before
 * submitting (mirrors the Signup confirm-mismatch check).
 */
export function ChangePasswordForm() {
  const change = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const confirmMismatch =
    confirmPassword.length > 0 && confirmPassword !== newPassword;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        },
      },
    );
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <PasswordFormField
        label="Current password"
        name="currentPassword"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={(e) => {
          setCurrentPassword(e.target.value);
          change.reset();
        }}
      />
      <PasswordFormField
        label="New password"
        name="newPassword"
        autoComplete="new-password"
        required
        minLength={8}
        hint="At least 8 characters."
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      <PasswordFormField
        label="Confirm new password"
        name="confirmPassword"
        autoComplete="new-password"
        required
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={confirmMismatch ? "Passwords don't match." : undefined}
      />
      {change.error ? (
        <p className="type-body-sm text-danger-strong mx-4">{change.error.message}</p>
      ) : change.isSuccess ? (
        <p className="type-body-sm text-text-muted mx-4">
          Password updated. Other devices have been signed out.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={change.isPending || confirmMismatch}>
          {change.isPending ? "Saving…" : "Update password"}
        </Button>
      </div>
    </form>
  );
}
